import { NotificationItem, NotificationSettings } from '../types/notifications';
import { notificationService } from './notificationService';
import { apiService } from '../lib/api';
// import { getInitialNotifications } from '../data/mockNotifications';

class NotificationHistoryService {
  private readonly STORAGE_KEY = 'chime_notifications_history';
  private readonly MAX_NOTIFICATIONS = 100; // Keep only last 100 notifications

  // Fetch notifications from backend API
  async fetchNotificationsFromBackend(): Promise<NotificationItem[]> {
    try {
      const response = await apiService.request<any>('/api/v1/user/notifications?page=1&limit=50');

      // Transform backend notification format to our NotificationItem format
      const notifications = response.notifications.map((notification: any) => ({
        id: notification.notificationId.toString(),
        type: this.mapBackendTypeToFrontend(notification.type),
        title: this.generateNotificationTitle(notification),
        message: notification.message || '',
        createdAt: notification.createdAt,
        isRead: notification.seenAt !== null,
        actor: notification.actor ? {
          id: notification.actor.userId,
          name: notification.actor.fullName || notification.actor.username,
          avatar: notification.actor.profilePicture
        } : undefined,
        actionData: notification.postId ? { postId: notification.postId } : undefined
      }));

      // Save to localStorage as cache
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(notifications));

      return notifications;
    } catch (error) {
      console.error('Failed to fetch notifications from backend:', error);
      // Fall back to localStorage
      return this.getNotifications();
    }
  }

  // Map backend notification types to frontend types
  private mapBackendTypeToFrontend(backendType: string): NotificationItem['type'] {
    const typeMap: Record<string, NotificationItem['type']> = {
      'upvote': 'upvote',
      'comment': 'comment',
      'poll_vote': 'poll_vote',
      'community': 'community_post',
      'exactech_news': 'exactech_news'
    };
    return typeMap[backendType] || 'community_post';
  }

  // Generate title from notification data
  private generateNotificationTitle(notification: any): string {
    switch (notification.type) {
      case 'upvote':
        return 'New Upvote!';
      case 'comment':
        return 'New Comment';
      case 'poll_vote':
        return 'New Poll Vote';
      default:
        return 'New Notification';
    }
  }

  // Get all notifications from localStorage (cached)
  getNotifications(): NotificationItem[] {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (stored) {
        const notifications = JSON.parse(stored);
        // Sort by creation date, newest first
        return notifications.sort((a: NotificationItem, b: NotificationItem) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        );
      }
    } catch (error) {
      console.warn('Failed to load notifications from storage:', error);
    }
    return [];
  }

  // Add a new notification
  async addNotification(notification: Omit<NotificationItem, 'id' | 'createdAt' | 'isRead'>): Promise<NotificationItem> {
    const settings = notificationService.getNotificationSettings();
    
    // Check if this type of notification is enabled
    const isEnabled = this.isNotificationTypeEnabled(notification.type, settings);
    if (!isEnabled) {
      // Don't store disabled notification types
      throw new Error(`Notifications of type ${notification.type} are disabled`);
    }

    const newNotification: NotificationItem = {
      ...notification,
      id: `notification_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      createdAt: new Date().toISOString(),
      isRead: false,
    };

    try {
      const currentNotifications = this.getNotifications();
      const updatedNotifications = [newNotification, ...currentNotifications].slice(0, this.MAX_NOTIFICATIONS);
      
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(updatedNotifications));
      
      // Also try to show browser notification if permissions are granted
      if (Notification.permission === 'granted') {
        notificationService.showNotification(notification.title, {
          body: notification.message,
          tag: newNotification.id,
          data: notification.actionData,
        });
      }
      
      return newNotification;
    } catch (error) {
      console.error('Failed to save notification:', error);
      throw error;
    }
  }

  // Mark notification as read
  async markAsRead(notificationId: string): Promise<void> {
    try {
      const notifications = this.getNotifications();
      const updatedNotifications = notifications.map(n => 
        n.id === notificationId ? { ...n, isRead: true } : n
      );
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(updatedNotifications));
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
      throw error;
    }
  }

  // Mark all notifications as read
  async markAllAsRead(): Promise<void> {
    try {
      const notifications = this.getNotifications();
      const updatedNotifications = notifications.map(n => ({ ...n, isRead: true }));
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(updatedNotifications));
    } catch (error) {
      console.error('Failed to mark all notifications as read:', error);
      throw error;
    }
  }

  // Delete a notification
  async deleteNotification(notificationId: string): Promise<void> {
    try {
      const notifications = this.getNotifications();
      const updatedNotifications = notifications.filter(n => n.id !== notificationId);
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(updatedNotifications));
    } catch (error) {
      console.error('Failed to delete notification:', error);
      throw error;
    }
  }

  // Clear all notifications
  async clearAllNotifications(): Promise<void> {
    try {
      localStorage.removeItem(this.STORAGE_KEY);
    } catch (error) {
      console.error('Failed to clear notifications:', error);
      throw error;
    }
  }

  // Get unread count
  getUnreadCount(): number {
    const notifications = this.getNotifications();
    return notifications.filter(n => !n.isRead).length;
  }

  // Get notifications by type
  getNotificationsByType(type: NotificationItem['type']): NotificationItem[] {
    const notifications = this.getNotifications();
    return notifications.filter(n => n.type === type);
  }

  // Check if notification type is enabled in settings
  private isNotificationTypeEnabled(type: NotificationItem['type'], settings: NotificationSettings): boolean {
    switch (type) {
      case 'upvote':
        return settings.upvotes;
      case 'comment':
        return settings.comments;
      case 'poll_vote':
        return settings.pollVotes;
      case 'community_post':
        return settings.communityPosts;
      case 'exactech_news':
        // ExacTech news notifications are always enabled (not configurable)
        return true;
      default:
        return false;
    }
  }

  // Simulate receiving notifications (for demo purposes)
  async simulateNotifications(): Promise<void> {
    const demoNotifications = [
      {
        type: 'upvote' as const,
        title: 'New Upvote!',
        message: 'Dr. Sarah Johnson upvoted your post about rotator cuff repair',
        actor: {
          id: 123,
          name: 'Dr. Sarah Johnson',
          avatar: 'https://i.pravatar.cc/40?u=sarah'
        },
        actionData: { postId: 341 }
      },
      {
        type: 'comment' as const,
        title: 'New Comment',
        message: 'Dr. Michael Chen commented on your shoulder surgery case',
        actor: {
          id: 456,
          name: 'Dr. Michael Chen',
          avatar: 'https://i.pravatar.cc/40?u=michael'
        },
        actionData: { postId: 341, commentId: 789 }
      },
      {
        type: 'community_post' as const,
        title: 'New Community Post',
        message: 'New discussion in Orthopedic Surgery: "Latest techniques in ACL reconstruction"',
        actionData: { postId: 567 }
      }
    ];

    // Add notifications with some delay to simulate real-world scenario
    for (let i = 0; i < demoNotifications.length; i++) {
      setTimeout(async () => {
        try {
          await this.addNotification(demoNotifications[i]);
        } catch (error) {
          console.log('Demo notification not added (likely disabled in settings):', error);
        }
      }, i * 2000); // 2 second delay between each
    }
  }
}

export const notificationHistoryService = new NotificationHistoryService();